import * as tslib_1 from "tslib";
import { HttpEventType, } from '@angular/common/http';
import { OnDestroy, OnInit, } from '@angular/core';
import { FormBuilder, Validators, } from '@angular/forms';
import { AppStateFacade, Setting, } from '@app-state';
import { UiModalComponent } from '@components/ui-modal/ui-modal.component';
import { ClientErrorCodes, MLInputType, MLLanguage, MLProcessor, } from '@models';
import { TranslateService } from '@ngx-translate/core';
import { MAX_NUMBER_OF_RESULTS } from '@pages/shared/constants';
import { FileUploadService, MLPackagesService, } from '@services';
import UiValidators, { UI_DESCRIPTION_MAX_LENGTH, UI_MLPACKAGE_NAME_REGEX, UI_NAME_MLPACKAGE_MAX_LENGTH, } from '@validators';
import cloneDeep from 'lodash-es/cloneDeep';
import { BehaviorSubject, of, Subject, timer, } from 'rxjs';
import { auditTime, catchError, delay, distinctUntilChanged, filter, flatMap, map, pairwise, pluck, share, skip, startWith, switchMap, takeUntil, tap, } from 'rxjs/operators';
var TRANSITION_SPEED = 250;
var MLPackageUploadModalComponent = /** @class */ (function (_super) {
    tslib_1.__extends(MLPackageUploadModalComponent, _super);
    function MLPackageUploadModalComponent() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.files = [];
        _this.loading$ = new BehaviorSubject(false);
        _this.isReadOnly$ = new Subject();
        _this.resultSize = MAX_NUMBER_OF_RESULTS;
        _this.processorTypes = [];
        _this.inputTypes = [];
        _this.languages = [];
        _this.isUploadingNewVersion = _this.context.data.name != null;
        _this._maxModelSizeInByte = 5368709120; // 5GB
        _this._allowedFileExtensions = ['.zip'];
        _this._mlpackagesService = _this._injector.get(MLPackagesService);
        _this._fileUploadService = _this._injector.get(FileUploadService);
        _this._translateService = _this._injector.get(TranslateService);
        _this._appState = _this._injector.get(AppStateFacade);
        _this._progressSource = new Subject();
        _this._destroyed$ = new Subject();
        _this._validateUniqueMLPackageName = function (control) {
            if (control.pristine || control.value == null) {
                return of(null);
            }
            return timer(500).pipe(switchMap(function () { return _this._mlpackagesService.checkUniqueMLPackageName(control.value); }), catchError(function (error) { return of(error); }), pluck('error', 'respCode'), 
            // 20005 means the ML package name is not unique
            map(function (respCode) { return respCode === 20005 ? { notUnique: true } : null; }), tap(function () { return _this._cd.markForCheck(); }));
        };
        return _this;
    }
    Object.defineProperty(MLPackageUploadModalComponent.prototype, "fileNames", {
        get: function () {
            return this.files.map(function (file) { return file.name; }).join(', ');
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(MLPackageUploadModalComponent.prototype, "selectedType", {
        get: function () {
            var _this = this;
            return this.form && this.processorTypes.filter(function (processorType) { return processorType.id === _this.form.get('processor').value[0].id; });
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(MLPackageUploadModalComponent.prototype, "selectedInputType", {
        get: function () {
            var _this = this;
            return this.inputTypes.filter(function (inputVal) { return inputVal.id === _this.context.data.inputType; });
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(MLPackageUploadModalComponent.prototype, "selectedLanguage", {
        get: function () {
            var _this = this;
            return this.form && this.languages.filter(function (language) { return language.id === _this.form.get('language').value[0].id; });
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(MLPackageUploadModalComponent.prototype, "packagingInstructionsUrl", {
        get: function () {
            return this._appState.settings.get(Setting.mlPackagingInstructionsUrl);
        },
        enumerable: true,
        configurable: true
    });
    MLPackageUploadModalComponent.prototype.ngOnInit = function () {
        var _this = this;
        // hide progress for fast uploads
        // and show the progress bar for multiple progress states
        this.showProgressBar$ = this._progressSource
            .pipe(startWith(0), map(function (progress) { return !!progress; }), distinctUntilChanged(), pairwise(), switchMap(function (_a) {
            var previous = _a[0], current = _a[1];
            return of(current).pipe(delay(!previous && current ? TRANSITION_SPEED : 0));
        }));
        // update progress according to the progress-bar transition speed
        this.progress$ = this._progressSource.pipe(auditTime(TRANSITION_SPEED));
        this.type = this.context.additionalData.type;
        // currently only CPU is supported, will add back GPU option when backend starts to support it
        this.processorTypes = [MLProcessor.CPU]
            .map(function (processor) { return ({
            id: processor,
            text: processor,
        }); });
        this.inputTypes = [MLInputType.json, MLInputType.file, MLInputType.files]
            .map(function (inputVal) { return ({
            // The value that will be sent to the backend
            id: inputVal,
            // The value that will be displayed
            text: Object.keys(MLInputType).find(function (key) { return MLInputType[key] === inputVal; }),
        }); });
        this.languages = [MLLanguage.PYTHON36, MLLanguage.PYTHON27]
            .map(function (language) { return ({
            // The value that will be sent to the backend
            id: Object.keys(MLLanguage).find(function (key) { return MLLanguage[key] === language; }),
            // The value that will be displayed
            text: language,
        }); });
        this.form = this._injector.get(FormBuilder).group({
            'name': [
                this.context.data.name,
                [
                    Validators.maxLength(UI_NAME_MLPACKAGE_MAX_LENGTH),
                    Validators.pattern(UI_MLPACKAGE_NAME_REGEX),
                    UiValidators.required(),
                ],
                [
                    this._validateUniqueMLPackageName,
                ],
            ],
            'inputType': [
                [this.context.data.inputType],
                UiValidators.required(),
            ],
            'inputDescription': [
                this.context.data.latestVersionInputDescription,
                Validators.maxLength(UI_DESCRIPTION_MAX_LENGTH),
            ],
            'outputDescription': [
                this.context.data.latestVersionOutputDescription,
                Validators.maxLength(UI_DESCRIPTION_MAX_LENGTH),
            ],
            'language': [
                this.languages,
                UiValidators.required(),
            ],
            'processor': [
                this.processorTypes,
                UiValidators.required(),
            ],
            'description': [
                '',
                Validators.maxLength(UI_DESCRIPTION_MAX_LENGTH),
            ],
            'changeLog': [
                '',
                Validators.maxLength(UI_DESCRIPTION_MAX_LENGTH),
            ],
        });
        this.isReadOnly$
            .pipe(takeUntil(this._destroyed$))
            .subscribe(function (isReadOnly) { return isReadOnly ?
            _this.form.disable() :
            _this.form.enable(); });
    };
    MLPackageUploadModalComponent.prototype.ngOnDestroy = function () {
        this._destroyed$.next();
        this._destroyed$.complete();
    };
    MLPackageUploadModalComponent.prototype.onClear = function () {
        this.files = [];
    };
    MLPackageUploadModalComponent.prototype.onFileChange = function (files) {
        if (files.length <= 1) {
            this.files = files;
        }
    };
    MLPackageUploadModalComponent.prototype.onSubmit = function () {
        var _this = this;
        var areFilesValid = this.files.every(function (file) { return _this._fileUploadService.isFileItemValid(file, _this._allowedFileExtensions); });
        if (!areFilesValid) {
            this._snack.error(this._translateService.instant("mlpackage_upload_modal_file_input_warning") + " (#" + ClientErrorCodes.InvalidFile + ")");
            return;
        }
        var modelFile = this.files[0];
        if (modelFile.size > this._maxModelSizeInByte) {
            this._snack.error(this._translateService.instant("file_size_too_large", {
                maxSize: '5GB',
            }));
            return;
        }
        this.loading$.next(true);
        this._progressSource.next(0);
        var signedUrl = "";
        // disable the form while upload is in progress
        this.isReadOnly$.next(true);
        var upload$ = this._mlpackagesService.generateSignedUrlForModelUpload(modelFile.name).pipe(flatMap(function (response) {
            signedUrl = response.value;
            return _this._mlpackagesService.uploadModelToSignedUrl(modelFile, signedUrl);
        }), skip(1), // skip the first preflight request
        share(), catchError(function (err) {
            _this._handleUploadError();
            return of(err);
        }), takeUntil(this._destroyed$));
        upload$.subscribe();
        // Handle upload process event
        upload$.pipe(filter(function (ev) { return ev.type === HttpEventType.UploadProgress && ev.total !== null; })).subscribe(function (ev) {
            var processEvent = ev;
            _this._progressSource.next(Math.round((processEvent.loaded * 100) / processEvent.total));
        });
        // Handle upload request completed
        upload$.pipe(filter(function (ev) { return ev.type === HttpEventType.Response; }), flatMap(function () {
            var payload = tslib_1.__assign({}, cloneDeep(_this.context.data), { modelVersions: [{
                        modelLanguage: _this.form.get('language').value[0].id,
                        outputDescription: _this.form.get('outputDescription').value,
                        inputDescription: _this.form.get('inputDescription').value,
                        changeLog: _this.form.get('changeLog').value,
                        processorType: _this.form.get('processor').value[0].id,
                    }], name: _this.form.get('name').value, description: _this.form.get('description').value });
            return _this.isUploadingNewVersion ?
                _this._mlpackagesService.createPackageVersion(_this.context.data.id, payload, signedUrl) :
                _this._mlpackagesService.createPackage(payload, signedUrl);
        }), catchError(function (err) {
            _this._handleUploadError();
            return of(err);
        }), delay(500)).subscribe(function () {
            _this._closeDialog();
        });
    };
    MLPackageUploadModalComponent.prototype._handleUploadError = function () {
        this._snack.error(this._translateService.instant("mlpackages_upload_failure"));
        this._closeDialog();
    };
    MLPackageUploadModalComponent.prototype._closeDialog = function () {
        this._progressSource.next(0);
        this.loading$.next(false);
        this._dialogRef.close({
            type: 1 /* Add */,
        });
    };
    return MLPackageUploadModalComponent;
}(UiModalComponent));
export { MLPackageUploadModalComponent };
